import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleLeft, faAngleLeft, faAngleRight, faAngleDoubleRight } from '@fortawesome/free-solid-svg-icons';

/**
 * Calcula las páginas visibles de forma compacta
 * Muestra: primera página, páginas cercanas a la actual, última página
 * Con separadores "..." cuando hay huecos
 */
const calculateVisiblePages = (currentPage, totalPages, maxVisible = 5) => {
  if (totalPages <= maxVisible) {
    // Si hay pocas páginas, mostrar todas
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  const pages = [];
  const halfVisible = Math.floor(maxVisible / 2);
  
  // Siempre incluir primera página
  pages.push(0);
  
  // Calcular rango alrededor de la página actual
  let startPage = Math.max(1, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 2, currentPage + halfVisible);
  
  // Ajustar si estamos cerca del inicio
  if (currentPage <= halfVisible + 1) {
    endPage = Math.min(maxVisible - 2, totalPages - 2);
  }
  
  // Ajustar si estamos cerca del final
  if (currentPage >= totalPages - halfVisible - 2) {
    startPage = Math.max(1, totalPages - maxVisible + 1);
  }
  
  // Añadir separador si hay hueco después de la primera página
  if (startPage > 1) {
    pages.push('...');
  }
  
  // Añadir páginas del rango
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }
  
  // Añadir separador si hay hueco antes de la última página
  if (endPage < totalPages - 2) {
    pages.push('...');
  }
  
  // Siempre incluir última página (si hay más de 1 página)
  if (totalPages > 1) {
    pages.push(totalPages - 1);
  }
  
  return pages;
};

/**
 * Componente de paginación compacta y reutilizable
 * 
 * @param {number} currentPage - Página actual (0-indexed)
 * @param {number} totalPages - Total de páginas
 * @param {number} totalElements - Total de elementos
 * @param {number} numberOfElements - Número de elementos en la página actual
 * @param {number} pageSize - Tamaño de página actual
 * @param {Function} onPageChange - Callback para cambiar página (recibe página 0-indexed)
 * @param {Function} onPageSizeChange - Callback para cambiar tamaño de página (opcional)
 * @param {Array} pageSizeOptions - Opciones de tamaño de página (default: [10, 25, 50, 100])
 */
const Pagination = ({
  currentPage,
  totalPages,
  totalElements,
  numberOfElements,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  summaryActions = null,
}) => {
  const { t } = useTranslation();

  // No renderizar si no hay páginas
  if (totalPages <= 0) {
    return null;
  }

  const visiblePages = calculateVisiblePages(currentPage, totalPages);
  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  const handlePageChange = (page, event) => {
    // Prevenir comportamiento por defecto del navegador que causa scroll
    if (event) {
      event.preventDefault();
    }
    if (page >= 0 && page < totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    if (onPageSizeChange && newSize !== pageSize) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <div className="pagination-container">
      <div className="pagination-controls">
        {/* Selector de tamaño de página */}
        {onPageSizeChange && (
          <div className="pagination-page-size">
            <label htmlFor="page-size-select" className="pagination-page-size-label">
              {t('common.pagination.show')}:
            </label>
            <select
              id="page-size-select"
              className="pagination-page-size-select"
              value={pageSize}
              onChange={handlePageSizeChange}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Botones de navegación */}
        <ul className="pagination">
          {/* Botón Primera página */}
          <li className={isFirstPage ? 'disabled' : ''}>
            <button
              type="button"
              onClick={(e) => handlePageChange(0, e)}
              className="pagination-button"
              disabled={isFirstPage}
              title={t('common.pagination.first')}
              aria-label={t('common.pagination.first')}
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </button>
          </li>

          {/* Botón Anterior */}
          <li className={isFirstPage ? 'disabled' : ''}>
            <button
              type="button"
              onClick={(e) => handlePageChange(currentPage - 1, e)}
              className="pagination-button"
              disabled={isFirstPage}
              title={t('common.pagination.previous')}
              aria-label={t('common.pagination.previous')}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
          </li>

          {/* Páginas visibles */}
          {visiblePages.map((page, index) => {
            if (page === '...') {
              return (
                <li key={`ellipsis-${index}`} className="pagination-ellipsis">
                  <span className="pagination-button">...</span>
                </li>
              );
            }

            return (
              <li key={page} className={page === currentPage ? 'active' : ''}>
                <button
                  type="button"
                  onClick={(e) => handlePageChange(page, e)}
                  className="pagination-button"
                  aria-label={`${t('common.pagination.page')} ${page + 1}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page + 1}
                </button>
              </li>
            );
          })}

          {/* Botón Siguiente */}
          <li className={isLastPage ? 'disabled' : ''}>
            <button
              type="button"
              onClick={(e) => handlePageChange(currentPage + 1, e)}
              className="pagination-button"
              disabled={isLastPage}
              title={t('common.pagination.next')}
              aria-label={t('common.pagination.next')}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </li>

          {/* Botón Última página */}
          <li className={isLastPage ? 'disabled' : ''}>
            <button
              type="button"
              onClick={(e) => handlePageChange(totalPages - 1, e)}
              className="pagination-button"
              disabled={isLastPage}
              title={t('common.pagination.last')}
              aria-label={t('common.pagination.last')}
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            </button>
          </li>
        </ul>
      </div>

      {/* Resumen de paginación */}
      <div className="pagination-summary-area">
        <div className="pagination-summary">
          <p>
            {t('common.pagination.showing', {
              count: numberOfElements,
              total: totalElements,
              page: currentPage + 1,
              totalPages: totalPages,
            })}
          </p>
        </div>
        {summaryActions && (
          <div className="pagination-summary-actions">
            {summaryActions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
